import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

import {
  ContentCellProps,
  PaginationProps,
  RankCellProps,
} from '@/components/customComponents'
import { RankBadge } from '@/components/customComponents/scalrTables/leaderboard/RankBadge'
import { Button } from '@/components/ui/button'

import { formatNumber } from '@/utils'

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps): JSX.Element => {
  const getVisiblePages = (): (number | string)[] => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const pages: (number | string)[] = []

    // Always show first page
    pages.push(1)

    if (currentPage <= 3) {
      // Show first few pages: 1, 2, 3, 4, ..., last
      pages.push(2, 3, 4)
      pages.push('...')
    } else if (currentPage >= totalPages - 2) {
      // Show last few pages: 1, ..., n-3, n-2, n-1, n
      pages.push('...')
      pages.push(totalPages - 3, totalPages - 2, totalPages - 1)
    } else {
      // Show pages around current: 1, ..., current-1, current, current+1, ..., last
      pages.push('...')
      pages.push(currentPage - 1, currentPage, currentPage + 1)
      pages.push('...')
    }

    // Always show last page
    pages.push(totalPages)

    return pages
  }

  const visiblePages = getVisiblePages()

  return (
    <div className="flex items-center justify-center space-x-1 py-4">
      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="h-8 w-8"
      >
        <IconChevronLeft className="h-4 w-4" />
      </Button>

      {visiblePages.map((page, index) => (
        <div key={index}>
          {page === '...' ? (
            <span className="px-2 text-sm text-gray-500">...</span>
          ) : (
            <Button
              variant={currentPage === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(page as number)}
              className="h-8 w-8 p-0"
              title={`Page ${page}`}
            >
              {page}
            </Button>
          )}
        </div>
      ))}

      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="h-8 w-8"
      >
        <IconChevronRight className="h-4 w-4" />
      </Button>
    </div>
  )
}

export const formatContent = ({
  content,
  type = 'text',
  truncate,
  country,
}: ContentCellProps): React.ReactNode => {
  switch (type) {
    case 'hour':
      return (
        <span>
          {formatNumber(content, type, false)}
          <span className="text-sm">hr</span>
        </span>
      )
    case 'number':
    case 'money':
    case 'percentage':
      return formatNumber(content, type, true, country)
    case 'text':
    default:
      return (
        <span
          className={`${truncate ? 'truncate max-w-[5rem] hover:max-w-none hover:whitespace-normal' : ''}`}
        >
          {content}
        </span>
      )
  }
}

export const RankCell = ({ index, showBadge }: RankCellProps): JSX.Element => {
  if (!showBadge) {
    return (
      <div className="flex justify-center items-center h-full">
        {RankBadge(index, false)}
      </div>
    )
  }

  return (
    <div className="flex justify-center items-center h-full">
      {index < 3 ? RankBadge(index, true) : RankBadge(index, false)}
    </div>
  )
}
